import type { TelemetryInstruction } from '@modules/prism/versions/2025-07-01/utils/telemetry/types.api';

/**
 * Converts onClick telemetry instructions to data attributes for click tracking
 *
 * @param telemetry - Array of telemetry instructions from the API
 * @returns Object with data-track-click-* attributes, or empty object if no onClick telemetry
 *
 * @example
 * ```tsx
 * <button {...telemetryToClickAttributes(button.telemetry)}>
 *   {button.label}
 * </button>
 * ```
 *
 * Output:
 * ```html
 * <button
 *   data-track-click-event="click"
 *   data-track-click-element="button"
 *   data-track-click-label="Submit Form"
 * >
 * ```
 */
export function telemetryToClickAttributes(
  telemetry: TelemetryInstruction[] | undefined,
): Record<string, string> {
  const clickInstruction = telemetry?.findLast(
    t => t.trigger === 'onClick' && t.type === 'event',
  );

  if (!clickInstruction || clickInstruction.type !== 'event') {
    return {};
  }

  const attributes: Record<string, string> = {};

  if (clickInstruction.data.click_element) {
    attributes['data-track-click-element'] = String(
      clickInstruction.data.click_element,
    );
  }

  if (clickInstruction.data.click_label) {
    attributes['data-track-click-label'] = String(
      clickInstruction.data.click_label,
    );
  }

  if (clickInstruction.data.click_area) {
    attributes['data-track-click-area'] = String(
      clickInstruction.data.click_area,
    );
  }

  if (clickInstruction.data.click_category) {
    attributes['data-track-click-category'] = String(
      clickInstruction.data.click_category,
    );
  }

  if (clickInstruction.data.click_position) {
    attributes['data-track-click-position'] = String(
      clickInstruction.data.click_position,
    );
  }

  return attributes;
}
